import classNames from 'classnames';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import {
  useDisplay,
  useTownAssets,
  useTownConfig,
  useAuth,
  useWebApp,
} from '../stores';
import usePush from '../stores/push/usePush';
import {
  appTooltipProps,
  config,
  usePrivileges,
} from '../utils';
import pagePrivilegesNeeded from '../utils/pagePrivilegesNeeded';
import CasesPageSubmenu from '../views/Cases/CasesPage.submenu';
import CommsPageSubmenu from '../views/Comm/CommsPage.submenu';
import PartnersPageSubmenu from '../views/Partners/PartnersPage.submenu';
import PollsPageSubmenu from '../views/Polls/PollsPage.submenu';
import ProfilePageSubmenu from '../views/Profile/ProfilePage.submenu';
import QrsPageSubmenu from '../views/Qrs/QrsPage.submenu';
import SettingsPageSubmenu from '../views/Settings/SettingsPage.submenu';
import TasksPageSubmenu from '../views/Tasks/TasksPage.submenu';
import TeamsPageSubmenu from '../views/Teams/TeamsPage.submenu';
import UsersPageSubmenu from '../views/Users/UsersPage.submenu';

import ClockText from './ClockText';
import { Column } from './display';
import {
  gcatCodeIconSrc,
} from './generic';
import HelpMenu from './HelpMenu';
import SideMenuNotif from './SideMenuNotif';
import SideMenuSubmenuFold from './SideMenuSubmenuFold';
import SupportButton from './SupportButton';
import SvgColorImg from './SvgColorImg';

// Submenu components - eagerly loaded for instant menu updates

const submenuComponents = {
  cases: CasesPageSubmenu,
  tasks: TasksPageSubmenu,
  qrs: QrsPageSubmenu,
  polls: PollsPageSubmenu,
  comm: CommsPageSubmenu,
  responders: TeamsPageSubmenu,
  users: UsersPageSubmenu,
  partners: PartnersPageSubmenu,
  settings: SettingsPageSubmenu,
  profile: ProfilePageSubmenu,
};

function SideMenu() {
  const { townConfig } = useTownConfig();
  const { assets } = useTownAssets();
  const { auth } = useAuth();
  const { privilegesReady, hasAnyPrivilege } = usePrivileges();
  const { mainMenu } = useDisplay();
  const { setNavigatingTo } = mainMenu;
  const { push: { wsSocketOpen } } = usePush();
  const {
    cstmrLbl,
    groupsMap,
    imagesMap,
    logoId,
    wideLogoId,
  } = townConfig;
  const {
    translate,
  } = assets;
  const { info: authInfo, addDeviceSetting } = auth;
  const { webApp } = useWebApp();
  const {
    update,
    updateAvailable,
    updating,
    installable,
    installed,
    rejectedInstall,
    suggestInstall,
    rejectInstall,
  } = webApp;
  const {
    cuid,
    grpGpid,
    grpLbl,
    gcatCode,
    username,
    fname,
    lname,
    // privlg,
  } = authInfo || {};

  // Session-only: an available update is still pending, so the notice returns on
  // the next load until the user actually reloads.
  const [updateDismissed, setUpdateDismissed] = useState(false);

  const installAnswered = !!authInfo?.settings?.device?.tutorials?.webAppInstall;
  const showUpdate = (updateAvailable || updating) && (!updateDismissed);
  const showInstall = installable && (!installed) && (rejectedInstall === null) && (!installAnswered);

  // SideMenu owns the decision of which notice (if any) to surface; SideMenuNotif
  // is only the button + popup chrome. Update takes precedence over install.
  const sideMenuNotif = useMemo(
    () => {
      if (showUpdate) {
        return (
          <SideMenuNotif
            icon="/images/svg/gui/refresh.svg"
            label={translate('updateSw')}
            title={translate('updateAvailable', 'statements')}
            dismissLabel={translate('close')}
            onPrimary={update}
            onDismiss={() => setUpdateDismissed(true)}
          />
        );
      }

      if (showInstall) {
        const answerInstall = () => addDeviceSetting({
          tutorials: {
            ...(authInfo?.settings?.device?.tutorials || {}),
            webAppInstall: true,
          },
        });
        return (
          <SideMenuNotif
            icon="/images/svg/gui/download.svg"
            label={translate('install')}
            title={translate('installAppTitle', 'appNotice')}
            body={translate('installAppBody', 'appNotice')}
            dismissLabel={translate('noThanks')}
            onPrimary={async () => {
              await answerInstall();
              suggestInstall();
            }}
            onDismiss={async () => {
              await answerInstall();
              rejectInstall();
            }}
          />
        );
      }

      return null;
    },
    [
      showUpdate, showInstall, translate, update, suggestInstall, rejectInstall,
      addDeviceSetting, authInfo,
    ],
  );

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { pathname } = location;
  const pathnames = pathname.split('/');
  const [, currentPage, currentSubPage] = pathnames;

  // Track selected page in local state for instant submenu updates. The submenu
  // of the page just left stays mounted as foldingPage while it folds shut
  const [selectedPage, setSelectedPage] = useState(currentPage);
  const selectedPageRef = useRef(currentPage);
  const [foldingPage, setFoldingPage] = useState(null);

  const selectPage = useCallback(
    (link) => {
      const previous = selectedPageRef.current;
      if (previous === link) return;
      selectedPageRef.current = link;
      setSelectedPage(link);
      setFoldingPage(submenuComponents[previous] ? previous : null);
    },
    [],
  );

  // Sync selectedPage when URL actually changes (direct navigation, back button, etc.)
  useEffect(
    () => {
      if (currentPage) selectPage(currentPage);
    },
    [currentPage, selectPage],
  );

  // The page list scrolls when the viewport is too short to show every entry, but
  // auto-hiding scrollbars give no hint that more is hidden. Each edge overlay (a
  // shadow gradient capped by a full-width rule) is shown only when content is
  // hidden past that edge. Opacity is written straight onto the refs to avoid
  // re-running the large menuContent memo on every scroll frame.
  const scrollRef = useRef(null);
  const topFadeRef = useRef(null);
  const bottomFadeRef = useRef(null);

  const refreshScrollAffordance = useCallback(
    () => {
      const el = scrollRef.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const overflowing = ((scrollHeight - clientHeight) > 1);
      const showTop = (overflowing && (scrollTop > 1));
      const showBottom = (overflowing && ((scrollTop + clientHeight) < (scrollHeight - 1)));
      if (topFadeRef.current) topFadeRef.current.style.opacity = showTop ? '1' : '0';
      if (bottomFadeRef.current) bottomFadeRef.current.style.opacity = showBottom ? '1' : '0';
    },
    [],
  );

  // Re-evaluate on viewport resize; submenu expand/collapse changes content
  // height, so selectedPage/currentSubPage are deps that re-run this too.
  useEffect(
    () => {
      refreshScrollAffordance();
      const el = scrollRef.current;
      if (!el) return undefined;
      const observer = new ResizeObserver(refreshScrollAffordance);
      observer.observe(el);
      return () => observer.disconnect();
    },
    [refreshScrollAffordance, selectedPage, currentSubPage],
  );

  const menuContent = useMemo(
    () => {
      // User's group icon
      let groupIconSrc;
      const group = groupsMap.get(grpGpid);
      if (group) groupIconSrc = imagesMap.get(group.logoId);

      const profileIcon = (groupIconSrc)
        ? {
            Icon: (iconProps) => (
              <img
                alt={`${grpLbl[0]}${grpLbl[1]}`}
                src={groupIconSrc}
                className={iconProps.className}
              />
            ),
          }
        : { imgSrc: gcatCodeIconSrc(gcatCode || 'user') };

      // landing: the sub page a bare /<link> would redirect to, linked directly to
      // skip the redirect's spinner frame
      const pages = [
        {
          imgSrc: '/images/svg/gui/dial.svg',
          label: translate('dashboard'),
          link: 'home',
        },
        {
          imgSrc: '/images/svg/gui/warningOutline.svg',
          label: translate('reports'),
          link: 'cases',
          landing: 'list',
        },
        {
          imgSrc: '/images/svg/gui/task.svg',
          label: translate('tasks'),
          link: 'tasks',
          landing: 'list',
        },
        {
          imgSrc: '/images/svg/gui/marker.svg',
          label: translate('placesEquipment'),
          link: 'qrs',
          landing: 'list',
        },
        {
          imgSrc: '/images/svg/gui/vote.svg',
          label: translate('polls'),
          link: 'polls',
          landing: 'settings',
        },
        {
          imgSrc: '/images/svg/gui/comm.svg',
          label: translate('publications'),
          link: 'comm',
          landing: 'faq',
        },
        {
          imgSrc: '/images/svg/gcat/people/support.svg',
          label: translate('responders'),
          link: 'responders',
          landing: 'teams',
        },
        {
          imgSrc: '/images/svg/gcat/people/users.svg',
          label: translate('users'),
          link: 'users',
          landing: 'members',
        },
        {
          imgSrc: '/images/svg/gui/handshake.svg',
          label: translate('partnerships'),
          link: 'partners',
        },
        {
          imgSrc: '/images/svg/gui/gear.svg',
          label: cstmrLbl ?? translate('settings'),
          link: 'settings',
          landing: 'custom',
        },

        {
          ...profileIcon,
          // label: username || '?',
          label: lname ? `${fname[0]}. ${lname}` : username, // TBD: username for admin
          link: 'profile',
          landing: 'info',
        },
      ];

      const currentPiid = searchParams.get('i');

      // Render function passed to submenu components
      const renderItems = (items) => items.map((subMenuLine) => {
        if (subMenuLine.hide) return null;

        const subMenuSelected = (subMenuLine.link === currentSubPage);
        const ThisSubIcon = subMenuLine.icon;

        const subTo = `/${selectedPage}/${subMenuLine.link}${currentPiid ? `?i=${currentPiid}` : ''}`;

        return (
          <Link
            className={classNames(
              'w-full flex flex-row gap-4 items-center justify-start relative py-2 no-underline text-inherit',
              {
                'fill-pxMainMenuItem': !subMenuSelected,
                'fill-pxTxt': subMenuSelected,
                'pointer-events-none': subMenuLine.disabled,
              },
            )}
            to={subTo}
            id={`sub_${subMenuLine.link}`}
            key={`sub_${subMenuLine.link}`}
          >
            {
              // selected line outline
              subMenuSelected
                ? (
                    <div className="absolute t-0 l-0 ml-16 h-full w-full py-0.5 pointer-events-none">
                      <div className="bg-pxMainBgnd rounded-l-md h-full w-full" />
                    </div>
                  )
                : null
            }

            {
              // sub menu icon or filler
              ThisSubIcon
                ? <ThisSubIcon className="ml-10 w-5 h-5 z-10 relative shrink-0" />
                : <div className="ml-10 w-5 h-5 z-10 relative shrink-0" />
            }

            <p
              className={classNames(
                'text-base z-10 relative truncate',
                {
                  'font-semibold': subMenuSelected,
                  'text-pxTxt': subMenuSelected,
                  'opacity-50': subMenuLine.disabled,
                },
              )}
            >
              {subMenuLine.label}
            </p>
          </Link>
        );
      });

      const menuPages = [];
      pages.forEach((thisPage) => {
        if (!thisPage.hide) {
          const ThisIcon = thisPage.Icon;
          const thisPageSelected = (thisPage.link === selectedPage);
          const SubmenuComponent = submenuComponents[thisPage.link];
          const privilegeNeeded = pagePrivilegesNeeded[thisPage.link];
          const dimmed = privilegeNeeded
                        && ((!privilegesReady) || (!hasAnyPrivilege(privilegeNeeded)));

          const onPageClick = (e) => {
            if (thisPage.disabled) {
              e.preventDefault();
              return;
            }
            if (thisPage.link !== selectedPage) {
              selectPage(thisPage.link);
              setNavigatingTo(thisPage.link);
            }
          };

          const pageClassName = classNames(
            'w-full flex flex-row gap-4 items-center justify-start relative py-2 no-underline text-inherit text-left',
            {
              'fill-pxMainMenuItem': !thisPageSelected,
              'fill-pxMainMenuItemSel': thisPageSelected,
              'opacity-50': thisPage.disabled || dimmed,
            },
          );

          const pageInner = (
            <>
              { thisPageSelected
                ? <div className="absolute t-0 l-0 bg-white/10 rounded-tl-md ml-3 h-full w-full pointer-events-none" />
                : null}

              { (ThisIcon)
                ? <ThisIcon className="object-contain ml-7 w-5 h-5 shrink-0 z-10" />
                : null}

              { (thisPage.imgSrc)
                ? (
                    <span className="relative ml-7 shrink-0 z-10">
                      <SvgColorImg
                        className={classNames(
                          'object-contain w-5 h-5',
                          {
                            'fill-pxMainMenuItemSel': thisPageSelected,
                            'fill-pxMainMenuItem': !thisPageSelected,
                          },
                        )}
                        src={thisPage.imgSrc}
                        description={thisPage.label}
                        imgColorAsCssFill
                      />

                      { thisPage.badge
                        ? <span className="absolute -top-1 -left-0.5 w-2 h-2 rounded-full px-glow-dot" />
                        : null}
                    </span>
                  )
                : null}

              <p
                className={classNames(
                  'truncate z-10',
                  {
                    'font-semibold': thisPageSelected,
                    'text-pxMainMenuItemSel': thisPageSelected,
                    'ml-16': !(ThisIcon || thisPage.imgSrc),
                  },
                )}
              >
                {thisPage.label}
              </p>
            </>
          );

          menuPages.push(
            <Link
              className={pageClassName}
              to={(thisPage.landing) ? `/${thisPage.link}/${thisPage.landing}` : `/${thisPage.link}`}
              onClick={onPageClick}
              id={thisPage.link}
              key={thisPage.link}
            >
              {pageInner}
            </Link>,
          );

          if (SubmenuComponent && (thisPageSelected || (thisPage.link === foldingPage))) {
            menuPages.push(
              // Single recess behind the whole submenu block: one graded overlay
              // (vs a per-row tint) avoids banding at each row boundary.
              <SideMenuSubmenuFold
                key={`submenu_${thisPage.link}`}
                open={thisPageSelected}
                onFolded={() => setFoldingPage(null)}
              >
                <div className="absolute top-0 left-0 ml-3 h-full w-full pointer-events-none bg-menuRecess" />
                <SubmenuComponent
                  renderItems={renderItems}
                  translate={translate}
                  authInfo={authInfo}
                />
              </SideMenuSubmenuFold>,
            );
          }
        }
      });

      const ret = (
        <>

          {(logoId && cuid)
            ? (
                <img
                  src={`${config.town.back.urlPrefix
                    + config.town.back.logos.get
                    + cuid
                  }/${
                    logoId || wideLogoId}`}
                  alt="logo"
                  className="h-10 max-w-9/10 shrink-0"
                />
              )
            : null}

          <div className="relative flex flex-col w-full flex-1 min-h-0">
            <div
              ref={topFadeRef}
              className="pointer-events-none absolute inset-x-0 top-0 z-20 h-4 opacity-0 transition-opacity duration-150 border-t border-pxMainMenuItem/20 bg-gradient-to-b from-black/50 to-transparent"
            />
            <div
              ref={scrollRef}
              onScroll={refreshScrollAffordance}
              className="flex flex-col items-center justify-start w-full flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
            >
              {menuPages }
            </div>
            <div
              ref={bottomFadeRef}
              className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-4 opacity-0 transition-opacity duration-150 border-b border-pxMainMenuItem/20 bg-gradient-to-t from-black/50 to-transparent"
            />
          </div>

          {sideMenuNotif}

          <HelpMenu />

          <div className="pt-2 relative">
            <img
              src="/images/png/logo.png"
              className={classNames(
                'w-20 shrink-0',
                { 'opacity-50': !wsSocketOpen },
              )}
              alt="logo Paxity"
            />

            {(!wsSocketOpen)
              ? (
                  <div
                    className="absolute top-1/2 -right-4 -translate-y-1/2 translate-x-1/2 w-8 h-8"
                    {...appTooltipProps(translate('noServerConnection', 'statements'))}
                  >
                    <SvgColorImg
                      src="/images/svg/gui/noAntenna.svg"
                      imgColor="#ff4800"
                      description={translate('noServerConnection', 'statements')}
                      className="w-full h-full"
                    />
                  </div>
                )
              : null}
          </div>

          <SupportButton />
        </>
      );

      return ret;
    },
    [
      cstmrLbl, groupsMap, grpGpid, imagesMap, grpLbl, translate, selectedPage,
      foldingPage, selectPage,
      setNavigatingTo, currentSubPage, username, gcatCode, logoId,
      fname, lname, authInfo, searchParams,
      wideLogoId, cuid, wsSocketOpen,
      privilegesReady, hasAnyPrivilege, sideMenuNotif,
      refreshScrollAffordance,
    ],
  );

  return (
    <Column
      start
      className="w-44 xl:w-48 2xl:w-60 shrink-0 relative z-10 bg-gradient-pxMenu shadow-[6px_0_22px_-6px_rgba(8,22,63,0.5)] text-pxMainMenuItem font-light text-lg overflow-hidden pt-6 text-left truncate"
    >
      {menuContent}

      <div className="py-2 relative">
        <ClockText
          className="text-pxMainMenuItem"
        />
      </div>
    </Column>
  );
}

export default SideMenu;
